import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useLocation, BrowserRouter } from "react-router-dom";
import { F as FooterProvider } from "./FooterProvider-CjpEJF0L.js";
import { useMutation, track, camelizeKeys, useQueryParams, FlowStateProvider, Step } from "@betterment/js-runtime";
import qs from "query-string";
import { Helmet } from "react-helmet";
import { CenteredWorkflowLayout, Text, InputGroup, Label, ErrorText, FormikCheckboxInput, Button, Box, Flash, HR, Flex } from "@betterment/style-closet-react";
import { Formik, Form, ErrorMessage } from "formik";
import pick from "lodash/pick";
import * as Yup from "yup";
import { S as SignupFlowHeader, a as FormikMaskedField, P as PhoneNumber } from "./PhoneNumber-CaezXveX.js";
import { F as Footer } from "./Footer-BPSPPtJk.js";
import { R as RedirectToPreviousStep } from "./RedirectToPreviousStep-BwydGlvM.js";
import { S as SecureInfoBadge } from "./SecureInfoBadge-DjTZpTAN.js";
import { F as FullWidthField } from "./Field-DmbDMqah.js";
import React, { useState, useEffect } from "react";
import { T as TrackableLink, u as urlBuilder, F as FullPageLoader } from "./index-BEglnXN9.js";
import { P as PasswordInputGroup, a as PasswordSchema } from "./PasswordInputGroup-DG4sHRWs.js";
import { c as convertApiErrorsToFormikMsg, a as overrideEmailErrorMessage, o as overridePhoneNumberErrorMessage } from "./form-helpers-C1M2v4FK.js";
import { Tooltip, IconButton } from "@betterment/samba-react";
import styled from "@emotion/styled";
import { F as FormikErrorFlashList } from "./FormikErrorFlashList-CICftIxB.js";
import { g as gtmTrack, S as SuccessStep } from "./SuccessStep-BlttH52e.js";
import { P as PhoneNumberSchema } from "./phone-number-schema-DfpW0Nhe.js";
import { v as vary, u as useFeatureFlag } from "./useFeatureFlag-B-iuTFlU.js";
const StepSchema$4 = Yup.object().shape({
  legalFirstName: Yup.string().required("Please enter your legal first name").min(1).max(125, "Please limit first name to 125 characters"),
  lastName: Yup.string().required("Please enter your legal last name").min(1).max(125, "Please limit last name to 125 characters"),
  hasPreferredFirstName: Yup.boolean(),
  preferredFirstName: Yup.string().when("hasPreferredFirstName", {
    is: true,
    then: (schema) => schema.required("Please enter your preferred first name").min(1).max(125, "Please limit preferred name to 125 characters"),
    otherwise: (schema) => schema.nullable()
  })
});
const ContactInfo = ({ flowState: { values }, goToNextStep }) => {
  if (!values.password) {
    return /* @__PURE__ */ jsx(RedirectToPreviousStep, {});
  }
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", heading: "Nice. Now, what's your name?", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
      /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: "We're required to get your full legal first and last name. If the name you go by is different, please enter both below." }),
      /* @__PURE__ */ jsx(
        Formik,
        {
          onSubmit: goToNextStep,
          validationSchema: StepSchema$4,
          initialValues: pick(values, Object.keys(StepSchema$4.getDefault())),
          validateOnBlur: false,
          validateOnChange: false,
          children: ({ values: formValues }) => /* @__PURE__ */ jsxs(Form, { children: [
            /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "legalFirstName", children: "Legal first name" }),
              /* @__PURE__ */ jsx(FullWidthField, { autoFocus: true, mb: "1", name: "legalFirstName", id: "legalFirstName", type: "text" }),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "legalFirstName", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "lastName", children: "Legal last name" }),
              /* @__PURE__ */ jsx(FullWidthField, { name: "lastName", mb: "1", id: "lastName", type: "text" }),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "lastName", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsx(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: /* @__PURE__ */ jsxs(Label, { htmlFor: "hasPreferredFirstName", children: [
              /* @__PURE__ */ jsx(
                FormikCheckboxInput,
                {
                  "aria-label": "I go by a different first name",
                  id: "hasPreferredFirstName",
                  name: "hasPreferredFirstName",
                  padding: "0"
                }
              ),
              /* @__PURE__ */ jsx(FormikCheckboxInput.Item, { children: "I go by a different first name" })
            ] }) }),
            formValues.hasPreferredFirstName && /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "preferredFirstName", children: "Okay, how should we address you?" }),
              /* @__PURE__ */ jsx(
                FullWidthField,
                {
                  autoFocus: true,
                  mb: "1",
                  name: "preferredFirstName",
                  id: "preferredFirstName",
                  type: "text",
                  "aria-label": "Your preferred first name"
                }
              ),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "preferredFirstName", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsx(Button, { type: "submit", width: ["100%", "16.6rem"], children: "Continue" }),
            /* @__PURE__ */ jsx(Box, { mt: "5", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const OfferFlash = ({
  flashVariant,
  headerText,
  subheaderText,
  offerTermsUrl
}) => {
  return /* @__PURE__ */ jsxs(Flash, { variant: flashVariant, icon: true, mt: "8", children: [
    /* @__PURE__ */ jsx(Text, { as: "h3", mb: "3", children: headerText }),
    /* @__PURE__ */ jsxs(Text, { as: "p", children: [
      subheaderText,
      " ",
      offerTermsUrl && /* @__PURE__ */ jsx(
        TrackableLink,
        {
          href: offerTermsUrl,
          target: "_blank",
          rel: "noopener noreferrer",
          eventProperties: { name: "SeeOfferTerms", flow: "SignupFlow" },
          children: "Offer Terms"
        }
      )
    ] })
  ] });
};
const SolicitorTerms = () => {
  return /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey50", mt: "8", children: `The website that directed you to Betterment is a Solicitor of Betterment ("Solicitor") and will receive up to $1,000 in compensation from Betterment LLC if you open an account through this page and fund the account. You will not be charged any fees for being referred. For Solicitor to receive compensation, you must not already have an account or relationship with Betterment or any of its affiliates. Solicitor has engaged with Betterment to promote Betterment's services, including but not limited to its investment advisory services. Solicitor may offer independent analysis and reviews of these services, but is neither offering any recommendations or investment advice based on your specific needs nor offering investment services, products, or advice on behalf of Betterment. Betterment and Solicitor are not under common ownership or otherwise related entities.` });
};
const StepSchema$3 = Yup.object().shape({
  email: Yup.string().required("Please enter an email address").email("Please enter a valid email address").max(125, "Please limit email to 125 characters"),
  password: PasswordSchema,
  userAgreement: Yup.boolean().oneOf([true], "Please accept")
});
const Credentials = ({
  flowState,
  goToNextStep,
  hasSolicitorTermsCookie,
  offerFlashContent,
  signupMotivationAccountType
}) => {
  const [submitEmail] = useMutation(urlBuilder.apiV2EmailSubmission.absolute(), { method: "post" });
  const [, setAssessment] = useState(null);
  const submitAndValidateEmail = async ({ email, signupMotivation }) => {
    const { errors } = await submitEmail({ email, signupMotivation });
    if (errors) {
      const response = convertApiErrorsToFormikMsg(errors, { overrideErrorMessage: overrideEmailErrorMessage });
      if (response.email === "has already been taken") {
        return {
          email: /* @__PURE__ */ jsxs(ErrorText, { children: [
            "There's already an account with this address. Please",
            " ",
            /* @__PURE__ */ jsx("a", { href: urlBuilder.appLogin(), rel: "noopener noreferrer", children: "log in" }),
            " ",
            "or use a different email."
          ] })
        };
      }
      return response;
    }
  };
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", heading: "Let's create your login.", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
      /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: signupMotivationAccountType === "advice" ? `We'll use this to connect you with one of our Betterment advisors.` : `We'll use this to start setting up your ${signupMotivationAccountType} account on Betterment.` }),
      /* @__PURE__ */ jsx(
        Formik,
        {
          onSubmit: goToNextStep,
          validationSchema: StepSchema$3,
          validate: submitAndValidateEmail,
          initialValues: pick(flowState.values, ["signupMotivation", ...Object.keys(StepSchema$3.getDefault())]),
          validateOnChange: false,
          validateOnBlur: false,
          children: (formProps) => /* @__PURE__ */ jsxs(Form, { noValidate: true, children: [
            /* @__PURE__ */ jsxs(Box, { mt: "4", mb: "6", children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "email", children: "Email address" }),
              /* @__PURE__ */ jsx(
                FullWidthField,
                {
                  autoFocus: true,
                  autoComplete: "off",
                  name: "email",
                  id: "email",
                  placeholder: "me@example.org",
                  type: "email",
                  inputMode: "email",
                  mb: "1"
                }
              ),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "email", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsx(
              PasswordInputGroup,
              {
                fullWidth: true,
                setAssessment,
                value: formProps.values.password || "",
                formProps
              }
            ),
            /* @__PURE__ */ jsxs(Box, { my: "9", children: [
              /* @__PURE__ */ jsxs(Label, { htmlFor: "userAgreement", children: [
                /* @__PURE__ */ jsx(
                  FormikCheckboxInput,
                  {
                    padding: "0",
                    "aria-labelledby": "userAgreement--label",
                    id: "userAgreement",
                    name: "userAgreement",
                    "data-testid": "userAgreement"
                  }
                ),
                /* @__PURE__ */ jsxs(FormikCheckboxInput.Item, { children: [
                  "By checking this box, you agree to our ",
                  /* @__PURE__ */ jsx(
                    TrackableLink,
                    {
                      href: urlBuilder.getLimitedScopeAgreement(),
                      target: "_blank",
                      rel: "noopener noreferrer",
                      eventProperties: { name: "privacyPolicy", flow: "SignupFlow" },
                      children: "Limited Scope Advisory Agreement"
                    }
                  ),
                  ", consent to electronic delivery of communications and our ",
                  /* @__PURE__ */ jsx(
                    TrackableLink,
                    {
                      href: urlBuilder.getPrivacyPolicy(),
                      target: "_blank",
                      rel: "noopener noreferrer",
                      eventProperties: { name: "privacyPolicy", flow: "SignupFlow" },
                      children: "Privacy Policy"
                    }
                  ),
                  ", and acknowledge that you have received a copy of our ",
                  /* @__PURE__ */ jsx(
                    TrackableLink,
                    {
                      href: urlBuilder.getLegalFormCrs(),
                      target: "_blank",
                      rel: "noopener noreferrer",
                      eventProperties: { name: "formCrs", flow: "SignupFlow" },
                      children: "Form CRS relationship summary"
                    }
                  ),
                  " and ",
                  /* @__PURE__ */ jsx(
                    TrackableLink,
                    {
                      href: urlBuilder.getAdv(),
                      target: "_blank",
                      rel: "noopener noreferrer",
                      eventProperties: { name: "formAdv", flow: "SignupFlow" },
                      children: "Form ADV brochure"
                    }
                  ),
                  "."
                ] })
              ] }),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "userAgreement", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsx(Button, { disabled: formProps.isValidating, type: "submit", width: ["100%", "16.6rem"], children: "Continue" }),
            /* @__PURE__ */ jsx(Box, { mt: "6", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
          ] })
        }
      ),
      (offerFlashContent || hasSolicitorTermsCookie) && /* @__PURE__ */ jsx(HR, { borderColor: "bmtGrey30", mt: "8" }),
      offerFlashContent && /* @__PURE__ */ jsx(OfferFlash, { ...offerFlashContent }),
      hasSolicitorTermsCookie && /* @__PURE__ */ jsx(SolicitorTerms, {})
    ] }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const StepSchema$2 = Yup.object().shape({
  email: Yup.string().required("Please enter an email address").email("Please enter a valid email address").max(125, "Please limit email to 125 characters")
});
const Email = ({
  flowState,
  goToNextStep,
  hasSolicitorTermsCookie,
  offerFlashContent
}) => {
  const [submitEmail] = useMutation(urlBuilder.apiV2EmailSubmission.absolute(), { method: "post" });
  const submitAndValidateEmail = async ({ email, signupMotivation }) => {
    const { errors } = await submitEmail({ email, signupMotivation });
    if (errors) {
      const response = convertApiErrorsToFormikMsg(errors, { overrideErrorMessage: overrideEmailErrorMessage });
      if (response.email === "has already been taken") {
        return {
          email: /* @__PURE__ */ jsxs(ErrorText, { children: [
            "There's already an account with this address. Please",
            " ",
            /* @__PURE__ */ jsx("a", { href: urlBuilder.appLogin(), rel: "noopener noreferrer", children: "log in" }),
            " ",
            "or use a different email."
          ] })
        };
      }
      return response;
    }
  };
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", heading: "Great, let's start with your email address.", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
      /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: "You'll use this as your login." }),
      /* @__PURE__ */ jsx(
        Formik,
        {
          onSubmit: goToNextStep,
          validationSchema: StepSchema$2,
          validate: submitAndValidateEmail,
          initialValues: pick(flowState.values, ["signupMotivation", ...Object.keys(StepSchema$2.getDefault())]),
          validateOnChange: false,
          validateOnBlur: false,
          children: ({ isValidating }) => /* @__PURE__ */ jsxs(Form, { noValidate: true, children: [
            /* @__PURE__ */ jsxs(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "email", children: "Email address" }),
              /* @__PURE__ */ jsx(
                FullWidthField,
                {
                  autoFocus: true,
                  autoComplete: "off",
                  name: "email",
                  id: "email",
                  placeholder: "me@example.org",
                  type: "email",
                  inputMode: "email",
                  mb: "1"
                }
              ),
              /* @__PURE__ */ jsx(ErrorMessage, { name: "email", component: ErrorText })
            ] }),
            /* @__PURE__ */ jsx(Button, { disabled: isValidating, type: "submit", width: ["100%", "16.6rem"], children: "Continue" }),
            /* @__PURE__ */ jsx(Box, { mt: "5", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
          ] })
        }
      ),
      (offerFlashContent || hasSolicitorTermsCookie) && /* @__PURE__ */ jsx(HR, { borderColor: "bmtGrey30", mt: "8" }),
      offerFlashContent && /* @__PURE__ */ jsx(OfferFlash, { ...offerFlashContent }),
      hasSolicitorTermsCookie && /* @__PURE__ */ jsx(SolicitorTerms, {})
    ] }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const trackSubmitClick = () => track("ElementClicked", { name: "SubmitSignupForm", flow: "SignupFlow" });
const StepSchema$1 = Yup.object().shape({
  userAgreement: Yup.boolean().oneOf([true], "Please accept")
});
const UserAgreement = ({
  flowState,
  goToNextStep,
  handleSubmit
}) => {
  if (!flowState.values.phoneNumber) {
    return /* @__PURE__ */ jsx(RedirectToPreviousStep, {});
  }
  const processForm = async (values, { setErrors }) => {
    if (!values.hasPreferredFirstName) {
      values.preferredFirstName = null;
    }
    const { data, errors } = await handleSubmit({ ...flowState.values, ...values });
    if (errors) {
      setErrors(convertApiErrorsToFormikMsg(errors));
      return;
    }
    if (data) {
      gtmTrack("signupCompleted", data.t);
    }
    goToNextStep(values);
  };
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", title: "User Agreement", heading: "Lastly, there are agreements.", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
      /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: "Please read and agree to the following terms." }),
      /* @__PURE__ */ jsx(
        Formik,
        {
          onSubmit: processForm,
          validationSchema: StepSchema$1,
          initialValues: flowState.values,
          enableReinitialize: true,
          children: ({ errors, isSubmitting }) => /* @__PURE__ */ jsxs(Form, { children: [
            /* @__PURE__ */ jsx(InputGroup, { fullWidth: true, mb: [2, 8, 9], children: /* @__PURE__ */ jsxs(Label, { htmlFor: "userAgreement", children: [
              /* @__PURE__ */ jsx(
                FormikCheckboxInput,
                {
                  padding: "0",
                  "aria-labelledby": "userAgreement--label",
                  id: "userAgreement",
                  name: "userAgreement",
                  autoFocus: true
                }
              ),
              /* @__PURE__ */ jsxs(FormikCheckboxInput.Item, { children: [
                "By checking this box, you agree to our ",
                /* @__PURE__ */ jsx(
                  TrackableLink,
                  {
                    href: urlBuilder.getLimitedScopeAgreement(),
                    target: "_blank",
                    rel: "noopener noreferrer",
                    eventProperties: { name: "privacyPolicy", flow: "SignupFlow" },
                    children: "Limited Scope Advisory Agreement"
                  }
                ),
                ", consent to electronic delivery of communications and our ",
                /* @__PURE__ */ jsx(
                  TrackableLink,
                  {
                    href: urlBuilder.getPrivacyPolicy(),
                    target: "_blank",
                    rel: "noopener noreferrer",
                    eventProperties: { name: "privacyPolicy", flow: "SignupFlow" },
                    children: "Privacy Policy"
                  }
                ),
                ", and acknowledge that you have received a copy of our ",
                /* @__PURE__ */ jsx(
                  TrackableLink,
                  {
                    href: urlBuilder.getLegalFormCrs(),
                    target: "_blank",
                    rel: "noopener noreferrer",
                    eventProperties: { name: "formCrs", flow: "SignupFlow" },
                    children: "Form CRS relationship summary"
                  }
                ),
                " and ",
                /* @__PURE__ */ jsx(
                  TrackableLink,
                  {
                    href: urlBuilder.getAdv(),
                    target: "_blank",
                    rel: "noopener noreferrer",
                    eventProperties: { name: "formAdv", flow: "SignupFlow" },
                    children: "Form ADV brochure"
                  }
                ),
                "."
              ] })
            ] }) }),
            /* @__PURE__ */ jsx(FormikErrorFlashList, { errors }),
            /* @__PURE__ */ jsx(Button, { type: "submit", onClick: trackSubmitClick, disabled: isSubmitting, width: ["100%", "16.6rem"], children: "Create account" }),
            /* @__PURE__ */ jsx(Box, { mt: "5", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const FullWidthMaskedField = styled(FormikMaskedField)`
  max-width: 100%;
`;
const submitAndValidatePhoneNumber = async (phoneNumberValue, submitPhoneNumber) => {
  const response = await submitPhoneNumber({ phoneNumber: phoneNumberValue });
  if (response.errors) {
    const result = convertApiErrorsToFormikMsg(response.errors, {
      overrideErrorMessage: overridePhoneNumberErrorMessage
    });
    return { message: result.phoneNumber, isSuccess: false };
  }
  return { message: "", isSuccess: true };
};
const createValidationSchema = (submitPhoneNumber) => {
  return Yup.object().shape({
    phoneNumber: PhoneNumberSchema.test({
      message: "Phone number is invalid",
      test: async (value) => {
        const result = await submitAndValidatePhoneNumber(value, submitPhoneNumber);
        return result.isSuccess;
      }
    }),
    legalFirstName: Yup.string().required("Please enter your legal first name").min(1).max(125, "Please limit first name to 125 characters"),
    lastName: Yup.string().required("Please enter your legal last name").min(1).max(125, "Please limit last name to 125 characters"),
    hasPreferredFirstName: Yup.boolean(),
    preferredFirstName: Yup.string().when("hasPreferredFirstName", {
      is: true,
      then: (schema) => schema.required("Please enter your preferred first name").min(1).max(125, "Please limit preferred name to 125 characters"),
      otherwise: (schema) => schema.nullable()
    })
  });
};
const NameAndPhoneNumber = ({ flowState, goToNextStep }) => {
  const [createUser] = useMutation(urlBuilder.apiV2User.absolute(), { method: "post" });
  const [validatePhoneNumber] = useMutation(urlBuilder.apiV2ValidatePhoneNumber.absolute());
  const validationSchema = createValidationSchema(validatePhoneNumber);
  const handleFlowSubmit = async (values, { setErrors }) => {
    const { data, errors } = await createUser({ ...flowState.values, ...values });
    if (errors) {
      setErrors(convertApiErrorsToFormikMsg(errors));
      return;
    }
    if (data) {
      gtmTrack("signupCompleted", data.t);
    }
    goToNextStep(values);
  };
  if (!flowState.values.email || !flowState.values.password) return /* @__PURE__ */ jsx(RedirectToPreviousStep, {});
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", heading: "Tell us about yourself.", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { children: /* @__PURE__ */ jsx(
      Formik,
      {
        onSubmit: handleFlowSubmit,
        validationSchema,
        initialValues: pick(
          flowState.values,
          "phoneNumber",
          "legalFirstName",
          "lastName",
          "hasPreferredFirstName",
          "preferredFirstName"
        ),
        validateOnBlur: false,
        validateOnChange: false,
        children: ({ isSubmitting, isValidating, values: formValues }) => /* @__PURE__ */ jsxs(Form, { children: [
          /* @__PURE__ */ jsxs(Box, { mt: "8", mb: "6", "data-testid": "foo", children: [
            /* @__PURE__ */ jsx(Label, { htmlFor: "legalFirstName", children: "Legal first name" }),
            /* @__PURE__ */ jsx(FullWidthField, { autoFocus: true, mb: "1", name: "legalFirstName", id: "legalFirstName", type: "text" }),
            /* @__PURE__ */ jsx(ErrorMessage, { name: "legalFirstName", component: ErrorText })
          ] }),
          /* @__PURE__ */ jsx(Box, { mb: "6", children: /* @__PURE__ */ jsxs(Label, { htmlFor: "hasPreferredFirstName", children: [
            /* @__PURE__ */ jsx(
              FormikCheckboxInput,
              {
                "aria-label": "I go by a different first name",
                id: "hasPreferredFirstName",
                name: "hasPreferredFirstName",
                padding: "0"
              }
            ),
            /* @__PURE__ */ jsx(FormikCheckboxInput.Item, { children: "I go by a different first name" })
          ] }) }),
          formValues.hasPreferredFirstName && /* @__PURE__ */ jsxs(Box, { maxWidth: "100%", mb: "6", children: [
            /* @__PURE__ */ jsx(Label, { htmlFor: "preferredFirstName", children: "Okay, how should we address you?" }),
            /* @__PURE__ */ jsx(
              FullWidthField,
              {
                mb: "1",
                name: "preferredFirstName",
                id: "preferredFirstName",
                type: "text",
                "aria-label": "Your preferred first name"
              }
            ),
            /* @__PURE__ */ jsx(ErrorMessage, { name: "preferredFirstName", component: ErrorText })
          ] }),
          /* @__PURE__ */ jsxs(Box, { mb: "6", children: [
            /* @__PURE__ */ jsx(Label, { htmlFor: "lastName", children: "Legal last name" }),
            /* @__PURE__ */ jsx(FullWidthField, { name: "lastName", mb: "1", id: "lastName", type: "text" }),
            /* @__PURE__ */ jsx(ErrorMessage, { name: "lastName", component: ErrorText })
          ] }),
          /* @__PURE__ */ jsxs(Box, { mb: "9", children: [
            /* @__PURE__ */ jsxs(Flex, { alignItems: "flex-end", mb: "3", children: [
              /* @__PURE__ */ jsx(Label, { htmlFor: "phoneNumber", mb: "0", children: "U.S. phone number" }),
              /* @__PURE__ */ jsxs(Tooltip, { children: [
                /* @__PURE__ */ jsx(Tooltip.Trigger, { asChild: true, children: /* @__PURE__ */ jsx(
                  IconButton,
                  {
                    css: { paddingBottom: "space.0" },
                    label: "Phone number",
                    name: "InfoCircle",
                    spacing: "compact"
                  }
                ) }),
                /* @__PURE__ */ jsx(Tooltip.Content, { align: "center", side: "top", children: 'Must be a U.S. phone number. By providing your number to Betterment, you agree Betterment may contact you by phone regarding opening, funding, and/or to otherwise service an account. You may request to be put on a "Do Not Call" list maintained by Betterment.' })
              ] })
            ] }),
            /* @__PURE__ */ jsx(FullWidthMaskedField, { name: "phoneNumber", id: "phoneNumber", inputMode: "decimal", variant: "phone" }),
            /* @__PURE__ */ jsx(ErrorMessage, { name: "phoneNumber", component: ErrorText })
          ] }),
          /* @__PURE__ */ jsx(
            Button,
            {
              type: "submit",
              onClick: trackSubmitClick,
              disabled: isValidating || isSubmitting,
              width: ["100%", "16.6rem"],
              children: "Continue"
            }
          ),
          /* @__PURE__ */ jsx(Box, { mt: "6", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const StepSchema = Yup.object().shape({
  password: PasswordSchema
});
const Password = ({ flowState, goToNextStep }) => {
  const [, setAssessment] = useState(null);
  if (!flowState.values.email) {
    return /* @__PURE__ */ jsx(RedirectToPreviousStep, {});
  }
  return /* @__PURE__ */ jsxs(CenteredWorkflowLayout, { variant: "five-column", title: "Password", heading: "Create a password for your login.", children: [
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "header", children: /* @__PURE__ */ jsx(SignupFlowHeader, {}) }),
    /* @__PURE__ */ jsxs(CenteredWorkflowLayout.Slot, { children: [
      /* @__PURE__ */ jsx(Text, { as: "p", color: "bmtGrey60", children: "To make this extra secure, please use symbols, uncommon words, and at least 8 characters." }),
      /* @__PURE__ */ jsx(
        Formik,
        {
          onSubmit: goToNextStep,
          initialValues: pick(flowState.values, Object.keys(StepSchema.getDefault())),
          validationSchema: StepSchema,
          children: (formProps) => /* @__PURE__ */ jsxs(Form, { children: [
            /* @__PURE__ */ jsx(
              PasswordInputGroup,
              {
                setAssessment,
                value: formProps.values.password || "",
                formProps,
                fullWidth: true
              }
            ),
            /* @__PURE__ */ jsx(Button, { type: "submit", width: ["100%", "16.6rem"], children: "Continue" }),
            /* @__PURE__ */ jsx(Box, { mt: "5", children: /* @__PURE__ */ jsx(SecureInfoBadge, {}) })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(CenteredWorkflowLayout.Slot, { name: "footer", children: /* @__PURE__ */ jsx(Footer, {}) })
  ] });
};
const Success = ({
  flowState,
  shouldDisplayTreatment = false,
  redirectTo
}) => {
  const name = flowState.values.hasPreferredFirstName ? flowState.values.preferredFirstName : flowState.values.legalFirstName;
  const message = shouldDisplayTreatment ? `Welcome to Betterment, ${name}` : "You finished setting up your account!";
  return /* @__PURE__ */ jsx(
    SuccessStep,
    {
      message,
      eventProperties: { name: "SignupSuccessful", flow: "SignupFlow" },
      redirectTo
    }
  );
};
const Variant = ({ children }) => /* @__PURE__ */ jsx(Fragment, { children });
const TestTrackExperiment = ({
  name,
  context,
  children
}) => {
  const [assignment, setAssignment] = useState(null);
  const [variantMap, defaultVariant] = React.Children.toArray(children).reduce(
    (result, child) => {
      const element = child;
      if ((element == null ? void 0 : element.type) === Variant) {
        if (element.props.default) {
          result[1] = element.props.name;
        }
        result[0] = {
          ...result[0],
          [element.props.name]: {
            element,
            handler: () => setAssignment(element.props.name)
          }
        };
      }
      return result;
    },
    [{}, null]
  );
  const variants = Object.keys(variantMap).reduce(
    (result, variantName) => ({
      ...result,
      [variantName]: variantMap[variantName].handler
      // eslint-disable-line @typescript-eslint/unbound-method
    }),
    {}
  );
  if (defaultVariant === null) {
    throw new Error("Must specify one Variant as the default");
  }
  const configuration = {
    context,
    variants,
    defaultVariant
  };
  useEffect(() => {
    vary(name, configuration);
  }, [name, configuration]);
  if (assignment) {
    return /* @__PURE__ */ jsx(Fragment, { children: variantMap[assignment].element });
  }
  return null;
};
TestTrackExperiment.Variant = Variant;
const signupValues = {
  email: "",
  password: "",
  legalFirstName: "",
  lastName: "",
  hasPreferredFirstName: false,
  preferredFirstName: "",
  phoneNumber: "",
  signupMotivation: null,
  accountInterest: null,
  userAgreement: false
};
const signupMotivationRedirectPaths = {
  advice: urlBuilder.appProfileSetupFlow({ product_category: "advice" }),
  checking: urlBuilder.appProfileSetupFlow({ product_category: "checking" }),
  investing: urlBuilder.appProfileSetupFlow({ product_category: "investing" }),
  goals_investing: urlBuilder.appProfileSetupFlow({ product_category: "investing" }),
  retirement: urlBuilder.appProfileSetupFlow({ product_category: "retirement" }),
  savings: urlBuilder.appProfileSetupFlow({ product_category: "cash" }),
  crypto: urlBuilder.appProfileSetupFlow({ product_category: "crypto" }),
  general_investing: urlBuilder.appProfileSetupFlow({ product_category: "general_investing" }),
  default: urlBuilder.getHome()
};
const signupMotivationAccountTypes = {
  advice: "advice",
  savings: "high-yield cash",
  bond_investing: "bond investing",
  general_investing: "investing",
  retirement: "IRA",
  crypto: "crypto investing",
  checking: "checking"
};
const getReturnToPath = (signupMotivation) => signupMotivationRedirectPaths[signupMotivation] ?? signupMotivationRedirectPaths.default;
const Controller = ({
  signupMotivations,
  accountInterests,
  offerFlashContent,
  hasSolicitorTermsCookie
}) => {
  const { enabled: isCryptoDeprecationEnabled } = useFeatureFlag({
    splitName: "retail.block_crypto_account_creation_q3_2024_enabled",
    context: "SignupFlow"
  });
  const [createUser] = useMutation(urlBuilder.apiV2User.absolute(), { method: "post" });
  const location = useLocation();
  const { signupMotivation, accountInterest } = camelizeKeys(qs.parse(location.search));
  const hasSignupMotivation = signupMotivations.includes(signupMotivation);
  const safeSignupMotivation = () => {
    if (!hasSignupMotivation) return null;
    if (signupMotivation === "crypto" && isCryptoDeprecationEnabled) return null;
    if (signupMotivation === "investing") return "goals_investing";
    return signupMotivation;
  };
  const hasAccountInterest = accountInterests.includes(accountInterest);
  const safeAccountInterest = hasAccountInterest ? accountInterest : null;
  const signupMotivationAccountType = hasSignupMotivation && signupMotivationAccountTypes[signupMotivation] ? signupMotivationAccountTypes[signupMotivation] : "";
  const returnToPath = getReturnToPath(signupMotivation);
  const { email = "" } = useQueryParams();
  const initialSignupValues = {
    ...signupValues,
    signupMotivation: safeSignupMotivation(),
    accountInterest: safeAccountInterest,
    email
  };
  const { enabled: isFeatureFlagEnabled, loading: isFeatureFlagLoading } = useFeatureFlag({
    splitName: "retail.simple_signup_consolidation_q3_2024_enabled",
    context: "SignupFlow"
  });
  const signupFlowControl = () => {
    return /* @__PURE__ */ jsxs(FlowStateProvider, { initialValues: initialSignupValues, returnToPath, children: [
      /* @__PURE__ */ jsx(Step, { name: "email", children: (stepProps) => /* @__PURE__ */ jsx(
        Email,
        {
          offerFlashContent,
          hasSolicitorTermsCookie,
          ...stepProps
        }
      ) }),
      /* @__PURE__ */ jsx(Step, { name: "password", component: Password }),
      /* @__PURE__ */ jsx(Step, { name: "contact_info", component: ContactInfo }),
      /* @__PURE__ */ jsx(Step, { name: "phone_number", component: PhoneNumber }),
      /* @__PURE__ */ jsx(Step, { name: "user_agreement", children: (props) => /* @__PURE__ */ jsx(UserAgreement, { ...props, handleSubmit: createUser }) }),
      /* @__PURE__ */ jsx(Step, { name: "success", children: (props) => /* @__PURE__ */ jsx(Success, { ...props, redirectTo: getReturnToPath(props.flowState.values.signupMotivation ?? "default") }) })
    ] });
  };
  const signupFlowExperiment = () => {
    return /* @__PURE__ */ jsxs(
      TestTrackExperiment,
      {
        name: "retail.simple_signup_consolidation_q3_2024_experiment",
        context: "SimpleSignUpExperiment",
        children: [
          /* @__PURE__ */ jsx(TestTrackExperiment.Variant, { name: "control", default: true, children: signupFlowControl() }),
          /* @__PURE__ */ jsx(TestTrackExperiment.Variant, { name: "treatment", children: /* @__PURE__ */ jsxs(FlowStateProvider, { initialValues: initialSignupValues, returnToPath, children: [
            /* @__PURE__ */ jsx(Step, { name: "credentials", children: (stepProps) => /* @__PURE__ */ jsx(
              Credentials,
              {
                offerFlashContent,
                hasSolicitorTermsCookie,
                signupMotivationAccountType,
                ...stepProps
              }
            ) }),
            /* @__PURE__ */ jsx(Step, { name: "contact_info", component: NameAndPhoneNumber }),
            /* @__PURE__ */ jsx(Step, { name: "success", children: (props) => /* @__PURE__ */ jsx(
              Success,
              {
                ...props,
                shouldDisplayTreatment: true,
                redirectTo: getReturnToPath(props.flowState.values.signupMotivation ?? "default")
              }
            ) })
          ] }) })
        ]
      }
    );
  };
  if (isFeatureFlagLoading) return /* @__PURE__ */ jsx(FullPageLoader, {});
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Helmet, { children: /* @__PURE__ */ jsx("title", { children: "Betterment | Sign Up" }) }),
    isFeatureFlagEnabled ? signupFlowExperiment() : signupFlowControl()
  ] });
};
const SignupFlowApp = (props) => {
  return /* @__PURE__ */ jsx(BrowserRouter, { basename: "app/signup", children: /* @__PURE__ */ jsx(FooterProvider, { children: /* @__PURE__ */ jsx(Controller, { ...props }) }) });
};
export {
  SignupFlowApp as default
};
//# sourceMappingURL=SignupFlowApp-BEl8nmRv.js.map
